import { useMemo } from "react";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCrudEditContext } from "@ryde/redux";
import { hasKeys, useTranslationConditional } from "@ryde/shared";

import { FIELD_ERROR_DUPLICATE_MESSAGE_LABEL_THRESHOLD } from "./error-display.constants";
import {
  accumulateFieldErrors,
  extractFieldNameFromPath,
} from "./error-display.utils";
import { IAccumulatedFieldError } from "./errors-display.models";

export const useAccumulatedTranslatedFormFieldMessages = (
  formFields: FieldErrors | undefined,
) => {
  const { tConditional } = useTranslationConditional();
  const { t } = useTranslation();
  const { entity } = useCrudEditContext();

  const messageList = useMemo((): IAccumulatedFieldError[] => {
    if (!formFields || !hasKeys(formFields)) {
      return [];
    }

    const translatedErrors = accumulateFieldErrors(formFields);

    return translatedErrors.map(error => {
      const duplicateMessageCount = translatedErrors.filter(
        x => x.message === error.message,
      ).length;
      const translatedMessage = tConditional(error.message);

      const message =
        duplicateMessageCount < FIELD_ERROR_DUPLICATE_MESSAGE_LABEL_THRESHOLD
          ? translatedMessage
          : `${translatedMessage} ${t(`${entity}.formLabel.${extractFieldNameFromPath(error.path)}`)}`;

      return { path: error.path, message };
    });
  }, [entity, formFields, t, tConditional]);

  return messageList;
};
